import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, GripVertical } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { ProductSize, CreateProductSizeData, UpdateProductSizeData } from "@/hooks/useProductSizes";

interface ProductSizeManagerProps {
  productId: string;
  sizes: ProductSize[];
  onCreateSize: (data: CreateProductSizeData) => Promise<ProductSize | null>;
  onUpdateSize: (data: UpdateProductSizeData) => Promise<ProductSize | null>;
  onDeleteSize: (sizeId: string) => Promise<boolean>;
  onReorderSizes: (sizeIds: string[]) => Promise<boolean>;
  loading?: boolean;
}

interface SizeFormData {
  size_display_name: string;
  price: string;
  original_price: string;
  is_available: boolean;
  sort_order: number;
}

export function ProductSizeManager({
  productId,
  sizes,
  onCreateSize,
  onUpdateSize,
  onDeleteSize,
  onReorderSizes,
  loading = false,
}: ProductSizeManagerProps) {
  const [editingSize, setEditingSize] = useState<ProductSize | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<SizeFormData>({
    size_display_name: "",
    price: "",
    original_price: "",
    is_available: true,
    sort_order: sizes.length + 1,
  });
  const { toast } = useToast();

  // Generate size name from display name (lowercase, replace spaces with underscores)
  const generateSizeName = (displayName: string): string => {
    return displayName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  const resetForm = () => {
    setFormData({
      size_display_name: "",
      price: "",
      original_price: "",
      is_available: true,
      sort_order: sizes.length + 1,
    });
    setEditingSize(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!formData.size_display_name.trim() || !formData.price) {
        throw new Error("Display name and price are required");
      }

      const price = parseFloat(formData.price);
      if (isNaN(price) || price <= 0) {
        throw new Error("Valid price is required");
      }

      const originalPrice = formData.original_price ? parseFloat(formData.original_price) : undefined;
      if (originalPrice !== undefined && (isNaN(originalPrice) || originalPrice <= 0)) {
        throw new Error("Valid original price is required");
      }

      const sizeData = {
        product_id: productId,
        size_name: generateSizeName(formData.size_display_name),
        size_display_name: formData.size_display_name.trim(),
        price,
        original_price: originalPrice,
        is_available: formData.is_available,
        sort_order: formData.sort_order,
      };

      if (editingSize) {
        const updatedSize = await onUpdateSize({
          id: editingSize.id,
          ...sizeData,
        });
        
        if (updatedSize) {
          toast({
            title: "Success",
            description: "Variant updated successfully",
          });
        } else {
          throw new Error("Failed to update variant");
        }
      } else {
        const newSize = await onCreateSize(sizeData);
        
        if (newSize) {
          toast({
            title: "Success",
            description: "Variant added successfully",
          });
        } else {
          throw new Error("Failed to create variant");
        }
      }

      resetForm();
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast({
        title: "Error",
        description: `Failed to save variant: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (size: ProductSize) => {
    setEditingSize(size);
    setFormData({
      size_display_name: size.size_display_name,
      price: size.price.toString(),
      original_price: size.original_price?.toString() || "",
      is_available: size.is_available,
      sort_order: size.sort_order,
    });
    setShowAddForm(true);
  };

  const handleDelete = async (sizeId: string) => {
    if (window.confirm("Are you sure you want to delete this variant?")) {
      const success = await onDeleteSize(sizeId);
      if (success) {
        toast({
          title: "Success",
          description: "Variant deleted successfully",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete variant",
          variant: "destructive",
        });
      }
    }
  };

  const handleToggleAvailability = async (size: ProductSize) => {
    const success = await onUpdateSize({
      id: size.id,
      is_available: !size.is_available,
    });
    
    if (success) {
      toast({
        title: "Success",
        description: `Variant ${!size.is_available ? 'enabled' : 'disabled'} successfully`,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to update variant availability",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Product Variants</h3>
        <Button
          type="button"
          onClick={() => setShowAddForm(true)}
          disabled={loading}
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Variant
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {editingSize ? "Edit Variant" : "Add New Variant"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="size_display_name">Variant Name *</Label>
                <Input
                  id="size_display_name"
                  value={formData.size_display_name}
                  onChange={(e) => setFormData({ ...formData, size_display_name: e.target.value })}
                  placeholder="e.g., Small, Medium, Large"
                  required
                />
                <div className="mt-1 text-xs text-gray-500">
                  Internal name will be auto-generated (e.g., "small", "medium", "large")
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Price (₹) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="299.00"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="original_price">Original Price (₹)</Label>
                  <Input
                    id="original_price"
                    type="number"
                    step="0.01"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                    placeholder="399.00"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_available"
                  checked={formData.is_available}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                />
                <Label htmlFor="is_available">Available for purchase</Label>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading}>
                  {editingSize ? "Update Variant" : "Add Variant"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Sizes List */}
      <div className="space-y-2">
        {sizes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No variants added yet. Click "Add Variant" to get started.
          </div>
        ) : (
          sizes.map((size) => (
            <Card key={size.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{size.size_display_name}</span>
                      <Badge variant={size.is_available ? "default" : "secondary"}>
                        {size.is_available ? "Available" : "Unavailable"}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      ₹{size.price}
                      {size.original_price && (
                        <span className="ml-2 line-through text-gray-400">
                          ₹{size.original_price}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleAvailability(size)}
                    disabled={loading}
                  >
                    {size.is_available ? "Disable" : "Enable"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(size)}
                    disabled={loading}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(size.id)}
                    disabled={loading}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
